import { motion, useReducedMotion } from 'framer-motion';
import type { DateInvite, PlannerAnswers, PublicUser, TheirPlanner } from '@/api/types';
import { Avatar } from '@/components/ui/Avatar';
import { cn } from '@/lib/cn';
import { confirmedSlot, slotLabel } from './slots';
import { choiceOf, propText, usePlannerCatalog, vibeOf } from './plannerData';
import { whenLabel } from './share';
import { NO_THEME, NO_THEME_LABEL, venueOf } from '@/features/venues/catalog';

type Answers = Pick<PlannerAnswers, 'vibe' | 'prop' | 'topics_love'> | Pick<TheirPlanner, 'vibe' | 'prop' | 'topics_love'>;

/** When the date is, as the ticket says it. */
export function ticketWhen(inv: DateInvite): { big: string; small: string; pending: boolean } {
  const slot = confirmedSlot(inv);
  if (slot || (inv.status === 'scheduled' && inv.starts_at)) {
    const l = slotLabel(slot?.starts_at ?? inv.starts_at!);
    return { big: l.time, small: l.day, pending: false };
  }
  if (inv.status === 'accepted' || (inv.slots ?? []).length === 0) return { big: 'Now-ish', small: whenLabel(inv.starts_at), pending: false };
  const host = inv.role === 'host' ? 'You pick' : `${inv.host.display_name} picks`;
  return { big: 'Pending', small: inv.status === 'awaiting_host' ? `${host} the time` : 'Times coming up', pending: true };
}

const TONE = {
  yellow: 'bg-yellow text-night',
  bone: 'bg-bone text-night',
} as const;
const CUT = { ground: 'bg-ground', surface: 'bg-surface' } as const;

/**
 * The date ticket (SPEC_V6_2 §2 finale; both tickets side by side in the
 * lobby): a flat printed ticket, stub and perforation included. It shows only
 * what the owner may share: vibe, prop, loved topics, matches — never spice
 * or the skipped topic.
 */
export function DateTicket({
  inv,
  owner,
  answers,
  title,
  testId = 'date-ticket',
  delay = 0,
  compact,
  tone = 'yellow',
  on = 'surface',
}: {
  inv: DateInvite;
  owner: PublicUser | null;
  answers: Answers | null | undefined;
  title: string;
  testId?: string;
  delay?: number;
  compact?: boolean;
  /** The paper it's printed on. */
  tone?: keyof typeof TONE;
  /** What the ticket sits on, so the notches are cut out of it. */
  on?: keyof typeof CUT;
}) {
  const reduce = useReducedMotion();
  const { props, topics } = usePlannerCatalog();
  const when = ticketWhen(inv);
  const vibe = vibeOf(answers?.vibe);
  const prop = propText(props, answers?.prop);
  const loved = (answers?.topics_love ?? []).map((t) => choiceOf(topics, t)?.label).filter(Boolean);
  const venue = venueOf(inv.venue);
  const matched = inv.planner?.picks_matched;
  const rows: { label: string; value: string; testId?: string }[] = [
    { label: 'Vibe', value: vibe ? vibe.label : '—', testId: `${testId}-vibe` },
    { label: 'Bringing', value: prop ?? '—', testId: `${testId}-prop` },
    { label: 'Into', value: loved.length ? loved.join(', ') : '—' },
    ...(venue ? [{ label: 'Where', value: venue.short, testId: `${testId}-venue` }] : inv.venue === NO_THEME ? [{ label: 'Where', value: NO_THEME_LABEL, testId: `${testId}-venue` }] : []),
  ];
  return (
    <motion.article
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 12, filter: 'blur(4px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      transition={{ duration: 0.3, ease: 'easeOut', delay }}
      className={cn('relative flex overflow-hidden rounded-card', TONE[tone])}
      aria-label={title}
      data-testid={testId}
    >
      <div className={cn('min-w-0 flex-1', compact ? 'p-4' : 'p-5 sm:p-6')}>
        <p className="text-[13px] font-semibold">Popin Dates</p>
        <p className={cn('truncate font-semibold leading-tight', compact ? 'text-[15px]' : 'text-[17px]')}>{title}</p>
        <div className={cn('flex flex-wrap items-baseline gap-x-2', compact ? 'mt-3' : 'mt-4')}>
          <p className={cn('tabular-nums', compact ? 'text-[22px] font-semibold leading-none' : 'font-display text-[44px] leading-[0.9]')} data-testid={`${testId}-when`}>
            {when.big}
          </p>
          <p className="text-[13px] font-medium tabular-nums">
            {when.small} · {inv.length_min} min
          </p>
        </div>
        <dl className={cn('grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 border-t-2 border-night/80', compact ? 'mt-3 pt-3' : 'mt-4 pt-4')}>
          {rows.map((r) => (
            <div key={r.label} className="contents" data-testid={r.testId}>
              <dt className="text-[13px] leading-6 opacity-70">{r.label}</dt>
              <dd className="min-w-0 truncate text-[15px] font-semibold leading-6">{r.value}</dd>
            </div>
          ))}
        </dl>
        {matched != null && (
          <p
            className={cn('inline-block -rotate-2 rounded-full border-2 border-night px-3 py-0.5 text-[13px] font-semibold', compact ? 'mt-3' : 'mt-4')}
            data-testid={`${testId}-matched`}
          >
            {matched === 3 ? 'You matched on all 3. Spooky.' : matched === 0 ? 'Opposites on all 3. This’ll be fun.' : `You matched on ${matched}/3`}
          </p>
        )}
      </div>
      {/* The stub: perforated, notched, "admit two". */}
      <div className="relative flex w-14 shrink-0 flex-col items-center justify-between border-l-2 border-dashed border-night/40 py-4 sm:w-16">
        <span className={cn('absolute -left-[11px] -top-[10px] h-5 w-5 rounded-full', CUT[on])} aria-hidden />
        <span className={cn('absolute -bottom-[10px] -left-[11px] h-5 w-5 rounded-full', CUT[on])} aria-hidden />
        {owner ? <Avatar avatar={owner.avatar} size={compact ? 32 : 36} label={owner.display_name} /> : <span />}
        <span className="text-[15px] font-semibold [writing-mode:vertical-rl]">Admit two</span>
      </div>
    </motion.article>
  );
}

/** Both tickets side by side (lobby, SPEC_V6_2 §5). */
export function TicketPair({ inv, me }: { inv: DateInvite; me: PublicUser }) {
  const other = inv.role === 'host' ? inv.guest : inv.host;
  const p = inv.planner;
  if (!p) return null;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1" data-testid="ticket-pair">
      <DateTicket inv={inv} owner={me} answers={p.mine} title="Your ticket" testId="ticket-mine" compact on="ground" />
      <DateTicket inv={inv} owner={other} answers={p.theirs} title={other ? `${other.display_name}’s ticket` : 'Their ticket'} testId="ticket-theirs" delay={0.1} compact tone="bone" on="ground" />
    </div>
  );
}
